export const MAX_DURATION_MINUTES = 300

const pad = (n: number) => String(n).padStart(2, '0')

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

export function minutesToTime(minutes: number): string {
  const normalized = ((minutes % 1440) + 1440) % 1440
  return `${pad(Math.floor(normalized / 60))}:${pad(normalized % 60)}`
}

export function formatFullDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatTime(iso: string): string {
  const d = new Date(iso)
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function applyTimeToIso(
  originalIso: string,
  time: string,
  startTime?: string,
): string {
  if (!time) return ''
  const d = new Date(originalIso)
  const [hours, minutes] = time.split(':').map(Number)
  d.setHours(hours, minutes, 0, 0)

  // Cross-midnight: if we have a startTime reference and this time is before it,
  // the time belongs to the next calendar day
  if (startTime) {
    const startMin = timeToMinutes(startTime)
    const thisMin = timeToMinutes(time)
    if (thisMin < startMin) {
      d.setDate(d.getDate() + 1)
    }
  }

  return d.toISOString()
}

export function computeTotalMinutes(
  _startIso: string,
  startTime: string,
  endTime: string,
): number | null {
  if (!startTime || !endTime) return null
  const startMin = timeToMinutes(startTime)
  const endMin = timeToMinutes(endTime)
  let diff = endMin - startMin
  if (diff <= 0) diff += 1440 // cross-midnight: add 24 hours
  if (diff > MAX_DURATION_MINUTES) return null
  return diff
}

export function clampEndTime(startTime: string, endTime: string): string {
  if (startTime === endTime) {
    return minutesToTime(timeToMinutes(startTime) + 1)
  }

  const startMin = timeToMinutes(startTime)
  const endMin = timeToMinutes(endTime)
  let diff = endMin - startMin
  if (diff <= 0) diff += 1440

  if (diff > MAX_DURATION_MINUTES) {
    return minutesToTime(startMin + MAX_DURATION_MINUTES)
  }

  return endTime
}

export function formatDuration(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  if (hours > 0) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`
  }
  return `${mins} minute${mins !== 1 ? 's' : ''}`
}
